/**
 * Cross-tab auth notifications.
 *
 * The confirmation link in the signup email opens a NEW tab, so the tab the
 * user was already working in (dashboard / workspace, with the Settings panel's
 * "Unverified" badge on screen) holds a `useAuth()` user whose
 * `emailConfirmedAt` is still null. Nothing told it otherwise, so the badge
 * stayed wrong until a full page reload.
 *
 * `BroadcastChannel` is the platform's own same-origin tab bus — the verifying
 * tab posts one message and every other tab of this app re-derives its session.
 * It carries no payload beyond the event kind on purpose: the receiving tab
 * re-fetches `/users/me` (via `useAuth().refresh()`), so a spoofed or stale
 * message can only cause a redundant fetch, never a client-side state lie.
 *
 * Unsupported environments (older Safari, jsdom) simply get no cross-tab
 * signal — {@link useEmailVerificationSync}'s focus refresh still covers them,
 * and it is also what covers confirming from a different browser or device.
 *
 * @module
 */

/** Same-origin channel name shared by every tab of this app. */
const CHANNEL = 'molecule_auth'

/** Messages carried on the {@link CHANNEL} bus. */
export interface AuthBroadcastMessage {
  /** The event kind. Receivers re-fetch the user rather than trusting a payload. */
  type: 'email-verified'
}

/** Whether this browser exposes the BroadcastChannel API. */
function isSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined'
}

/**
 * Tell every other tab of this app that the signed-in user's email is now
 * confirmed. A no-op where BroadcastChannel is unavailable.
 */
export function broadcastEmailVerified(): void {
  if (!isSupported()) return
  try {
    const channel = new BroadcastChannel(CHANNEL)
    channel.postMessage({ type: 'email-verified' } satisfies AuthBroadcastMessage)
    channel.close()
  } catch (_error) {
    // Best-effort: a blocked/unavailable channel must never break the page that
    // just successfully confirmed the email.
  }
}

/**
 * Subscribe to auth broadcasts from other tabs.
 *
 * @param handler - Called with each message posted by another tab.
 * @returns An unsubscribe function (safe to call even when unsupported).
 */
export function onAuthBroadcast(handler: (message: AuthBroadcastMessage) => void): () => void {
  if (!isSupported()) return () => {}
  let channel: BroadcastChannel
  try {
    channel = new BroadcastChannel(CHANNEL)
  } catch (_error) {
    return () => {}
  }
  const listener = (event: MessageEvent): void => {
    const message = event.data as AuthBroadcastMessage | null
    if (message?.type === 'email-verified') handler(message)
  }
  channel.addEventListener('message', listener)
  return () => {
    channel.removeEventListener('message', listener)
    channel.close()
  }
}
